import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaUser, FaPlus, FaSignOutAlt, FaEdit, FaNewspaper, FaEye, FaEyeSlash, FaExclamationTriangle, FaCheckCircle, FaTimesCircle, FaClock } from "react-icons/fa";
import { useContributorAuth } from "../../context/ContributorAuthContext";

const ContributorDashboard = () => {
  const [articles, setArticles] = useState([]);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const navigate = useNavigate();
  const { contributor, loading, logoutContributor } = useContributorAuth();

  useEffect(() => {
    if (!loading && !contributor) {
      navigate("/loginContributor", { replace: true });
      return;
    }
    if (!loading && contributor) {
      fetchArticles();
    }
  }, [contributor, loading]);

  const fetchArticles = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/article/get-articles`,
        {
          method: "GET",
          credentials: "include",
        }
      );
      if (response.ok) {
        const data = await response.json();
        setArticles(data);
      } else {
        console.error("Failed to fetch articles");
      }
    } catch (error) {
      console.error("Error fetching articles:", error);
    }
  };

  const handleEditProfile = () => {
    navigate("/edit-profile");
  };

  const handlePostArticle = () => {
    navigate("/post-article");
  };

  const handleEditArticle = (id) => {
    console.log(id);
    navigate(`/edit-article/${id}`);
  };

  const toggleExpand = (id) => {
    setExpandedArticle(expandedArticle === id ? null : id);
  };

  const handleLogout = async () => {
    try {
      await logoutContributor();
      toast.success("Successfully logged out!");
      navigate("/search", { replace: true });
    } catch (error) {
      console.error("Error during logout:", error);
      toast.error("Logout failed. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <header className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center py-4 sm:py-6 gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <img src="/logo.png" alt="Dsportshub Logo" className="h-10 sm:h-12" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Contributor Dashboard</h1>
                <p className="text-gray-600 text-xs sm:text-sm">Manage your articles and profile</p>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
              <button 
                onClick={handleEditProfile} 
                className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium hover:bg-blue-700 transition-all duration-300 flex items-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <FaUser className="w-3 h-3 sm:w-4 sm:h-4" /> <span className="hidden xs:inline">Edit</span> Profile
              </button>
              <button 
                onClick={handlePostArticle} 
                className="bg-green-600 text-white px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium hover:bg-green-700 transition-all duration-300 flex items-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <FaPlus className="w-3 h-3 sm:w-4 sm:h-4" /> <span className="hidden xs:inline">Post</span> Article
              </button>
              <button 
                onClick={handleLogout} 
                className="bg-red-600 text-white px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium hover:bg-red-700 transition-all duration-300 flex items-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <FaSignOutAlt className="w-3 h-3 sm:w-4 sm:h-4" /> Logout
              </button>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3 mb-2">
            <FaNewspaper className="text-[#0071bc]" />
            My Articles
          </h2>
          <p className="text-gray-600 text-sm sm:text-base">Manage and track your published articles</p>
        </div>

        {articles.length === 0 ? (
          <div className="text-center py-10 sm:py-16 px-4 bg-white rounded-2xl shadow-lg border-2 border-dashed border-gray-300">
            <FaNewspaper className="text-5xl sm:text-6xl text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">No Articles Yet</h3>
            <p className="text-gray-600 mb-6 text-sm sm:text-base">Start by posting your first article to share your insights with the community.</p>
            <button 
              onClick={handlePostArticle}
              className="bg-[#0071bc] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold hover:bg-[#005a94] transition-all duration-300 flex items-center gap-2 mx-auto shadow-lg hover:shadow-xl transform hover:scale-105 text-sm sm:text-base"
            >
              <FaPlus /> Post Your First Article
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {articles.map((article) => {
              const getStatusInfo = (status) => {
                switch (status) {
                  case 1:
                    return { icon: FaCheckCircle, color: 'text-green-600', bg: 'bg-green-100', border: 'border-green-200', text: 'Approved' };
                  case 2:
                    return { icon: FaTimesCircle, color: 'text-red-600', bg: 'bg-red-100', border: 'border-red-200', text: 'Rejected' };
                  default:
                    return { icon: FaClock, color: 'text-yellow-600', bg: 'bg-yellow-100', border: 'border-yellow-200', text: 'Pending' };
                }
              };
              
              const statusInfo = getStatusInfo(article.status);
              const StatusIcon = statusInfo.icon;
              
              return (
                <div key={article.article_id} className="bg-white border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                  <div 
                    className="p-4 sm:p-6 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                    onClick={() => toggleExpand(article.article_id)}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
                      <div className="flex-1">
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{article.title}</h3>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                          <div className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium border ${statusInfo.bg} ${statusInfo.color} ${statusInfo.border}`}>
                            <StatusIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            {statusInfo.text}
                          </div>
                          <span className="text-xs sm:text-sm text-gray-500">
                            {expandedArticle === article.article_id ? (
                              <><FaEyeSlash className="inline mr-1" /> Hide Details</>
                            ) : (
                              <><FaEye className="inline mr-1" /> View Details</>
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 sm:gap-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditArticle(article.article_id);
                          }}
                          className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium hover:bg-blue-700 transition-all duration-300 flex items-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                          <FaEdit className="w-3 h-3 sm:w-4 sm:h-4" /> Edit
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {expandedArticle === article.article_id && (
                    <div className="border-t border-gray-200 p-4 sm:p-6 bg-gradient-to-r from-gray-50 to-blue-50">
                      <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Article Details</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <p className="text-sm font-medium text-gray-500 mb-1">Summary</p>
                          <p className="text-gray-700">{article.summary}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 mb-1">Category</p>
                          <p className="text-gray-700">{article.category}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 mb-1">Date Published</p>
                          <p className="text-gray-700">{article.datePublished}</p>
                        </div>
                        {article.status === 2 && article.rejectionReason && (
                          <div>
                            <p className="text-sm font-medium text-red-600 mb-1">Rejection Reason</p>
                            <p className="text-red-700 bg-red-50 p-3 rounded-lg border border-red-200">{article.rejectionReason}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default ContributorDashboard;
